// src/core-pages/VerifyEmail.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../Security/firebase-config";
import { httpsCallable } from "firebase/functions";
import { functions } from "../Security/firebase-config";
import CustomAlert from "../components/core-components/CustomAlert";

import "./VerifyEmail.css";

export default function VerifyEmail() {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ message: "", type: "" });

  const { search } = useLocation();
  const navigate = useNavigate();

  // ✅ After success, always send to /account unless caller provided something else.
  const next = new URLSearchParams(search).get("next") || "/account";

  // Prevent double auto-run in React 18 StrictMode (dev)
  const autoRanRef = useRef(false);

  const showAlert = (message, type = "info") => {
    setAlert({ message, type });
    setTimeout(() => setAlert({ message: "", type: "" }), 6000);
  };

  const runVerificationFlow = async () => {
    const user = auth.currentUser;

    if (!user) {
      showAlert("Please log in first, then come back to verify your email.", "error");
      navigate(
        `/login?next=${encodeURIComponent(`/verify-email?next=${encodeURIComponent(next)}`)}`,
        { replace: true }
      );
      return;
    }

    setLoading(true);
    try {
      // Refresh auth state (emailVerified updates only after reload)
      await user.reload();

      if (!user.emailVerified) {
        showAlert(
          "Not verified yet. Click the verification link in your email, then come back here (or refresh) and we’ll unlock access.",
          "error"
        );
        return;
      }

      // Call Cloud Function to grant verified access (EarlyRelease)
      const grant = httpsCallable(functions, "grantVerifiedAccess");
      const res = await grant();

      console.log("✅ grantVerifiedAccess response:", res?.data);

      showAlert("✅ Email verified! Your account has been upgraded.", "success");

      // ✅ Automatically send them to account
      navigate(next, { replace: true });
    } catch (err) {
      console.error("❌ VerifyEmail flow error:", err);

      const msg =
        err?.code === "functions/failed-precondition"
          ? "Your email still isn't verified yet. Please click the verification link, then try again."
          : err?.code === "functions/unauthenticated"
          ? "You must be logged in to complete verification. Please log in again."
          : "Could not confirm verification yet. Try again in a moment.";

      showAlert(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const user = auth.currentUser;
    if (!user) {
      showAlert("Please log in first.", "error");
      return;
    }

    setLoading(true);
    try {
      const { sendEmailVerification } = await import("firebase/auth");
      await sendEmailVerification(user);
      showAlert("Verification email resent. Check your inbox/spam.", "success");
    } catch (err) {
      console.error("❌ Resend verification failed:", err);
      showAlert("Failed to resend verification email.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Auto-check on page load
  useEffect(() => {
    if (autoRanRef.current) return;
    autoRanRef.current = true;

    runVerificationFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="verify-email">
      <div className="verify-email__card">
        {alert.message && (
          <CustomAlert
            message={alert.message}
            type={alert.type}
            onClose={() => setAlert({ message: "", type: "" })}
          />
        )}

        <h2 className="verify-email__title">Verify your email</h2>

        <p className="verify-email__subtitle">
          To access gated content (team features, premium tools, and Early Release access), you must verify
          your email address.
        </p>

        <p className="verify-email__hint">
          We’ve sent you a verification email. Click the link in that email, then return to this page — we’ll
          automatically check and unlock your account.
        </p>

        <div className="verify-email__actions">
          <button
            className="verify-email__btn verify-email__btn--primary"
            onClick={runVerificationFlow}
            disabled={loading}
          >
            {loading ? "Checking..." : "Check verification now"}
          </button>

          <button className="verify-email__btn" onClick={handleResend} disabled={loading}>
            Resend email
          </button>

          <button
            className="verify-email__btn verify-email__btn--ticket"
            onClick={() => navigate("/feedback")}
            disabled={loading}
          >
            Having issues? Create a ticket
          </button>
        </div>
      </div>
    </div>
  );
}